import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, ArrowCounterClockwise, Lock, Sparkle } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { CLUB_CATALOG } from "../lib/clubs";
import { useAuth } from "../contexts/AuthContext";

export default function ClubBag() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isPro =
    user?.role === "admin" ||
    ["active", "trialing"].includes(user?.subscription_status);
  const [yardages, setYardages] = useState({});
  const [loading, setLoading] = useState(isPro);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isPro) return;
    (async () => {
      try {
        const { data } = await api.get("/auth/me/clubs");
        setYardages(data?.yardages || {});
      } catch (err) {
        setError(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [isPro]);

  const setClubYards = (key, raw) => {
    if (raw === "" || raw == null) {
      setYardages((y) => {
        const { [key]: _, ...rest } = y;
        return rest;
      });
      return;
    }
    const n = parseInt(raw, 10);
    if (Number.isFinite(n)) {
      setYardages((y) => ({ ...y, [key]: n }));
    }
  };

  const fillDefaults = () => {
    const next = {};
    CLUB_CATALOG.forEach((c) => {
      next[c.key] = yardages[c.key] ?? c.default;
    });
    setYardages(next);
  };

  const save = async () => {
    setError("");
    setSaving(true);
    setSaved(false);
    try {
      await api.put("/auth/me/clubs", { yardages });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="px-5 pt-6 pb-8">
      <button
        type="button"
        onClick={() => nav(-1)}
        data-testid="clubbag-back"
        className="btn-ghost -ml-2 mb-3"
      >
        <ArrowLeft size={20} weight="bold" />
      </button>

      {!isPro ? (
        <div data-testid="clubbag-pro-gate">
          <p className="label-uppercase">Pro feature</p>
          <h1 className="font-heading font-black tracking-tighter-2 text-4xl text-[var(--primary)] mb-2">
            Your club bag
          </h1>
          <p className="text-sm text-[var(--text-secondary)] mb-6">
            Save your average carry distance for every club and we&apos;ll suggest the right one on every shot — adjusted for wind in real time.
          </p>
          <div className="surface-card p-5 mb-5">
            <div className="flex items-center gap-2 mb-3">
              <Sparkle size={18} weight="fill" className="text-[var(--accent-terracotta)]" />
              <h3 className="font-heading font-bold text-[var(--primary)]">Pro unlocks</h3>
            </div>
            <ul className="space-y-1.5 text-sm">
              {[
                "Save yardages for every club in your bag",
                "Smart in-round club suggestion",
                "Wind-adjusted recommendations",
              ].map((p) => (
                <li key={p} className="flex items-start gap-2">
                  <Check size={14} weight="bold" className="text-[var(--primary)] shrink-0 mt-0.5" />
                  <span className="text-[var(--text)]">{p}</span>
                </li>
              ))}
            </ul>
          </div>
          <Link
            to="/subscribe"
            data-testid="clubbag-go-pro"
            className="btn-primary w-full"
          >
            <Lock size={16} weight="bold" /> Go Pro to unlock
          </Link>
        </div>
      ) : (
        <>
      <p className="label-uppercase">Set up</p>
      <h1 className="font-heading font-black tracking-tighter-2 text-4xl text-[var(--primary)] mb-1">Your club bag</h1>
      <p className="text-sm text-[var(--text-secondary)] mb-5">
        Enter your average <strong>full-swing carry</strong> for each club. Leave any you don&apos;t carry blank — we&apos;ll only suggest what&apos;s in your bag.
      </p>

      <button
        type="button"
        onClick={fillDefaults}
        data-testid="clubbag-fill-defaults"
        className="btn-secondary mb-5"
      >
        <ArrowCounterClockwise size={16} weight="bold" /> Fill with UK averages
      </button>

      {loading ? (
        <div className="text-[var(--text-secondary)]">Loading…</div>
      ) : (
        <div className="space-y-2" data-testid="club-list">
          {CLUB_CATALOG.map((c) => (
            <div
              key={c.key}
              className="surface-card-flat flex items-center gap-3 px-4 py-2.5"
            >
              <div className="flex-1">
                <div className="font-heading font-bold text-[var(--primary)] text-sm">{c.label}</div>
                <div className="text-[11px] text-[var(--text-secondary)]">Avg {c.default} yds</div>
              </div>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  inputMode="numeric"
                  min={30}
                  max={400}
                  step={1}
                  value={yardages[c.key] ?? ""}
                  onChange={(e) => setClubYards(c.key, e.target.value)}
                  placeholder="—"
                  data-testid={`club-yds-${c.key}`}
                  className="input-field !w-20 !py-2 !text-center font-mono-num font-bold"
                />
                <span className="text-xs text-[var(--text-secondary)] font-bold">yds</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="text-sm text-[var(--status-bad)] bg-red-50 border border-red-200 rounded-xl px-4 py-3 mt-4">
          {error}
        </div>
      )}

      <button
        type="button"
        onClick={save}
        disabled={saving}
        data-testid="clubbag-save"
        className="btn-primary w-full mt-5"
      >
        {saved ? (
          <>
            <Check size={18} weight="bold" /> Saved
          </>
        ) : saving ? (
          "Saving…"
        ) : (
          "Save bag"
        )}
      </button>

      <p className="text-[11px] text-[var(--text-secondary)] mt-3 text-center">
        Used to suggest your club on the Round screen — adjusted for wind in real time.
      </p>
        </>
      )}
    </div>
  );
}
